import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, CalendarBlank, Ticket } from "@phosphor-icons/react";
import WaiverStep from "@/components/WaiverStep";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [missing, setMissing] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [donation, setDonation] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [showWaiver, setShowWaiver] = useState(false);

  useEffect(() => {
    axios.get(`${API}/site/events/${slug}`).then(({ data }) => setEvent(data)).catch(() => setMissing(true));
  }, [slug]);

  useEffect(() => {
    const sid = params.get("event_session");
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/site/events/status/${sid}`);
        if (data.status === "paid") { setDone(true); return; }
      } catch { /* keep polling */ }
      if (++tries < 12) setTimeout(poll, 2500);
    };
    poll();
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 1 || !form.email.includes("@")) { toast.error("Name and email required"); return; }
    setShowWaiver(true);
  };

  const doSignup = async (waiverInfo) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/site/events/${slug}/signup`, {
        ...form,
        amount_cents: event.mode === "donation" ? Math.round((parseFloat(donation) || 0) * 100) : 0,
        origin_url: window.location.origin,
        waiver_signature_id: waiverInfo.waiver_signature_id,
      });
      if (data.status === "free") setDone(true);
      else if (data.url) window.location.href = data.url;
    } catch (err) {
      setShowWaiver(false);
      toast.error(err?.response?.data?.detail || "Couldn't sign up — please try again.");
    } finally { setBusy(false); }
  };

  if (missing) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grid place-items-center px-6 text-center">
      <div><h1 className="text-2xl font-bold text-[#FFE01A] uppercase">Event not found</h1>
        <p className="text-[#A1A1AA] mt-2">This event may have ended. <a href="/" className="text-[#FFE01A]">Back to the site</a></p></div>
    </div>
  );
  if (!event) return <div className="min-h-screen bg-[#0A0A0A]" />;

  const soldOut = event.spots_left !== null && event.spots_left <= 0;
  const priceLabel = event.mode === "free" ? "Free entry" : event.mode === "donation" ? "Pay what you want" : `$${(event.price_cents / 100).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="event-page">
      <div className="max-w-xl mx-auto px-5 py-12">
        <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#FFE01A] mb-3">The Self-Agency Project · Special Event</div>
        <h1 className="text-3xl sm:text-4xl font-black uppercase leading-tight" data-testid="event-title">{event.title}</h1>
        <div className="mt-4 flex flex-col gap-2 text-[#D4D4D8]">
          <span className="flex items-center gap-2"><CalendarBlank size={17} color="#FFE01A" weight="fill" /> {event.date}{event.time ? ` at ${event.time}` : ""}</span>
          {event.location && <span className="flex items-center gap-2"><MapPin size={17} color="#FFE01A" weight="fill" /> {event.location}</span>}
          <span className="flex items-center gap-2"><Ticket size={17} color="#FFE01A" weight="fill" /> {priceLabel}
            {event.spots_left !== null && !soldOut && <span className="text-xs text-[#A1A1AA]">· {event.spots_left} spots left</span>}
          </span>
        </div>
        {event.description && <p className="mt-5 text-[#A1A1AA] whitespace-pre-line">{event.description}</p>}

        <div className="mt-8 border border-[#FFE01A]/40 bg-[#FFE01A]/5 p-5">
          {done ? (
            <div className="text-center py-4" data-testid="event-success">
              <div className="text-4xl">🎟️</div>
              <div className="text-xl font-bold text-[#FFE01A] mt-2">You're in!</div>
              <p className="text-[#A1A1AA] mt-1">Check your email for your ticket and all the details.</p>
            </div>
          ) : soldOut ? (
            <div className="text-center py-4 text-[#A1A1AA] font-bold uppercase" data-testid="event-soldout">Sold out</div>
          ) : showWaiver ? (
            <div>
              <button onClick={() => setShowWaiver(false)} className="text-xs underline text-white/60 bg-transparent border-0 cursor-pointer mb-2" data-testid="waiver-back-button">&larr; Back to details</button>
              <WaiverStep name={form.name} email={form.email} phone={form.phone}
                source="event" refInfo={event.title || slug} busy={busy}
                submitLabel={event.mode === "donation" && (parseFloat(donation) || 0) > 0 ? "Sign & continue to payment" : "Sign & reserve my spot"}
                onSigned={doSignup} />
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-3">
              <div className="font-bold uppercase tracking-wide text-sm">Reserve your spot</div>
              <input data-testid="event-name" required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-black border border-white/15 focus:border-[#FFE01A] outline-none text-white px-4 py-3 text-sm" />
              <input data-testid="event-email" required type="email" placeholder="Email (your ticket goes here)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-black border border-white/15 focus:border-[#FFE01A] outline-none text-white px-4 py-3 text-sm" />
              <input data-testid="event-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-black border border-white/15 focus:border-[#FFE01A] outline-none text-white px-4 py-3 text-sm" />
              {event.mode === "donation" && (
                <input data-testid="event-donation" type="number" min="0" step="1" placeholder="Your donation amount $ (0 = free entry)"
                  value={donation} onChange={(e) => setDonation(e.target.value)}
                  className="bg-black border border-white/15 focus:border-[#FFE01A] outline-none text-white px-4 py-3 text-sm" />
              )}
              <button type="submit" disabled={busy} data-testid="event-submit"
                className="bg-[#FFE01A] text-black font-bold uppercase tracking-wide px-6 py-3.5 cursor-pointer border-0 disabled:opacity-50">
                {busy ? "One sec…" : event.mode === "free" ? "Sign me up" : event.mode === "donation" ? "Continue" : `Pay ${priceLabel} & reserve`}
              </button>
            </form>
          )}
        </div>
        <div className="mt-6 text-center text-xs text-[#71717A]"><a href="/" className="text-[#FFE01A]">selfagency.ca</a></div>
      </div>
    </div>
  );
}
